import { NumberFormat } from '@lightspeed/i18n-number-format';

import Localize from 'app/src/localize/Localize';
import Monitoring from 'shared_modules/Monitoring';

import MultiCurrencyFieldBase from './multi_currency_field_base';
import MultiCurrencyFieldBaseStatic from './multi_currency_field_base_static';

const MultiCurrency = (): MultiCurrency => {
  const currencySymbolCache: MultiCurrencyCache[] = [];

  let i18nNumberFormatFormatter: NumberFormat;
  let locale = '';

  const getCurrencySymbol = ({
    currencyCode,
    currencyProvider,
    useCache = true,
  }: MultiCurrencyGetCurrencySymbolInput) => {
    const symbolCache = currencySymbolCache.find(
      ({ currencyCode: cacheCurrencyCode }) => cacheCurrencyCode === currencyCode
    );

    const locale = getLocale();

    let currencySymbol = currencyCode;

    if (useCache && symbolCache) {
      return symbolCache.currencySymbol;
    }

    try {
      switch (currencyProvider) {
        case 'i18nNumberFormat':
          if (!i18nNumberFormatFormatter) {
            i18nNumberFormatFormatter = new NumberFormat({ locale });
          }

          const { symbol } = i18nNumberFormatFormatter.getParts('0', {
            style: 'currency',
            currencyCode,
          });

          currencySymbol = symbol;
          break;

        case undefined:
        case 'Intl':
          // Cannot cache Intl.NumberFormat because it uses `currencyCode` in the constructor
          const intlFormatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
          });

          const numberParts = intlFormatter.formatToParts(0);

          const { value } = numberParts.find(({ type }) => type === 'currency') || {};

          if (value) {
            currencySymbol = value;
          }

          break;

        default:
          break;
      }
    } catch (error) {
      Monitoring.NRError(error, 'getCurrencySymbol', {
        currencyCode,
        locale,
      });
    }

    if (useCache) {
      currencySymbolCache.push({
        currencyCode,
        currencySymbol,
      });
    }

    return currencySymbol;
  };

  // Match https://github.com/merchantos/webPOS/blob/develop-retail/assets/js/apps/helpers/format.js#L52
  const getLocale = () => {
    if (!locale) {
      locale = window?.merchantos?.locale?.lang ?? 'en-US';
    }
    return locale;
  };

  const getSystemCurrencySymbol = () => Localize.getCurrencySymbol();

  return {
    fieldBase: MultiCurrencyFieldBase(),
    fieldBaseStatic: MultiCurrencyFieldBaseStatic(),
    getCurrencySymbol,
    getLocale,
    getSystemCurrencySymbol,
  };
};

export default MultiCurrency;

window.merchantos.multiCurrency = MultiCurrency();
